// ui/coach.ts — first-run teaching note.
//
// Extracted verbatim from an inline <script> in index.html. Assigns
// window.Coach. Reads only DOM signals, so it stays decoupled from client.ts.

export {};

/* ---------------------------------------------------------------------------
 * Coach — first-run teaching note.
 *
 * WHAT IT DOES
 *   Shows one short line at a time in the top-centre of the map, advancing
 *   when the player does the thing rather than on a timer alone. Five notes,
 *   then it retires itself permanently. A sixth line interrupts the sequence
 *   once, the first time a road goes un-buildable, because "Route too short
 *   for elevation change" in the panel is jargon a new player can't act on.
 *
 * WHY IT TOUCHES NOTHING
 *   No client.ts changes. The signals it needs are already on the page as
 *   readouts client.ts keeps current, so this module watches those instead of
 *   asking for callbacks:
 *
 *     edit   #m-length changes value while a canvas gesture is in flight
 *     add    #cp-used goes up (CPBudget already writes it on every mutation)
 *     steep  #infeasible-warn becomes display:block
 *     done   a click reaches #btn-submit / #btn-submit-top
 *
 *   The gesture gate on `edit` is the load-bearing part. #m-length is also
 *   rewritten on puzzle load and whenever a pan or zoom settles and the
 *   analysis re-runs at full precision — neither is the player editing the
 *   road, and without the gate both would advance the tutorial past notes
 *   nobody read. Panning never changes the value, so the two conditions
 *   together are the definition of "the player altered the alignment".
 *
 *   This is a deliberate trade: coupling to four element IDs instead of four
 *   call sites in client.ts. Those IDs are the ones the whole panel is built
 *   from — if any of them moves, the game is visibly broken well before the
 *   tutorial is. A console warning fires if they're missing.
 *
 * PERSISTENCE
 *   The step index is stored, not just a done flag, so a reload part-way
 *   through resumes where it was rather than restarting the sequence. Any of
 *   dismissal, completion, or a submit attempt writes 'done', which is final.
 *
 * MANUAL MODE
 *   The arrows page back and forth by hand. The first press latches manual
 *   mode for the rest of the session, which switches off both the dwell
 *   timers and the action jumps — a player who paged back to re-read a note
 *   is reading, and either mechanism firing underneath them would take the
 *   note away again. Manual mode is not persisted: it's a statement about
 *   this reading, not a preference.
 * ------------------------------------------------------------------------ */
(window as any).Coach = (function () {
  'use strict';

  var STORE = 'coach.v1';

  var box     = document.getElementById('coach');
  var eyebrow = document.getElementById('coach-eyebrow');
  var bodyEl  = document.getElementById('coach-body');
  var textEl  = document.getElementById('coach-text');
  var closeEl = document.getElementById('coach-x') as HTMLButtonElement | null;
  var prevEl  = document.getElementById('coach-prev') as HTMLButtonElement | null;
  var nextEl  = document.getElementById('coach-next') as HTMLButtonElement | null;
  var canvas  = document.getElementById('map');
  var mLength = document.getElementById('m-length');
  var cpUsed  = document.getElementById('cp-used');
  var warnEl  = document.getElementById('infeasible-warn');

  if (!box || !eyebrow || !bodyEl || !canvas) {
    console.warn('[Coach] Markup missing — the first-run note is inactive.');
    return null;
  }

  /* Notes, in order.
   *   key     dictionary entry for the body
   *   dwell   ms before advancing on its own; 0 waits indefinitely
   *   next    index to move to when dwell expires, -1 to finish
   *   onEdit  index to jump to when the player alters the alignment
   *   onAdd   index to jump to when a control point appears
   *   floor   ms during which action jumps are ignored (default FLOOR)
   *
   * The drag note comes fourth because its copy presupposes a control point
   * to drag, and note 2 is what puts one there. That ordering is also why it
   * needs a longer floor than the rest: by the time it appears the player is
   * already editing, so without one the very gesture that earned the note
   * would dismiss it before it could be read.
   *
   * Dwells are set from the length of the English string at roughly three
   * words a second, which is why the structures note gets the longest one. */
  var FLOOR = 1500;

  var NOTES = [
    { key: 'instr_intro',      dwell: 17000, next: 1, onEdit: 1, vals: marginVals },
    { key: 'coach_points',     dwell: 17000, next: 2, onAdd: 2, vals: cpVals },
    { key: 'coach_structures', dwell: 18000, next: 3 },
    { key: 'coach_drag',       dwell: 13000, next: 4, onEdit: 4, floor: 4500 },
    { key: 'coach_submit',     dwell: 11000, next: -1 }
  ];

  function cpVals() {
    return { n: (window.CPBudget && window.CPBudget.LIMIT) || 10 };
  }

  /* instr_intro's {t} is the cost margin needed to win, owned by client.ts as
   * WIN_COST_MARGIN and published on window. Read lazily, at render time: this
   * module is imported before client.ts's body runs, so reading it at module
   * scope would always see undefined. The 30 fallback matches the constant's
   * current value and only shows if client.ts failed to load, in which case
   * the note is the least of the problems. */
  function marginVals() {
    return { t: (window as any).RB_WIN_MARGIN_PCT || 30 };
  }

  var idx      = 0;      /* current note, or -1 once finished */
  var timer    = null;
  var floorTimer = null;
  var jumpable = false;  /* false until the current note has had its floor */
  var paused   = false;  /* true while the steep interrupt is on screen */
  var manual   = false;  /* true once the player has used the arrows */
  var edits    = 0;
  var steepUsed = false;

  function t(key) { return window.I18N ? I18N.t(key) : key; }
  function fill(key, vals) {
    return t(key).replace(/\{(\w+)\}/g, function (m, k) {
      return vals && vals[k] != null ? String(vals[k]) : m;
    });
  }

  function load() {
    try {
      var v = localStorage.getItem(STORE);
      if (v === 'done') return -1;
      var n = parseInt(v, 10);
      return (isFinite(n) && n >= 0 && n < NOTES.length) ? n : 0;
    } catch (e) { return 0; }
  }
  function save(v) {
    try { localStorage.setItem(STORE, v === -1 ? 'done' : String(v)); } catch (e) {}
  }

  function clearTimer() {
    if (timer !== null) { clearTimeout(timer); timer = null; }
    if (floorTimer !== null) { clearTimeout(floorTimer); floorTimer = null; }
  }

  /* Replay the fade. The class has to come off, the layout has to be read
   * back, and only then can it go on again — without the reflow the browser
   * coalesces both mutations into no change at all and the animation never
   * restarts. Same trick as the CPBudget flash. */
  function swap() {
    if (!textEl) return;
    textEl.classList.remove('coach-fade');
    void textEl.offsetWidth;
    textEl.classList.add('coach-fade');
  }

  /* The arrows are disabled at the ends rather than wrapping around or
   * doubling as dismiss: × is the way out, and a control that changes job at
   * the last note is a control you have to think about. */
  function syncNav() {
    if (prevEl) {
      prevEl.disabled = (idx <= 0);
      prevEl.setAttribute('aria-label', t('coach_prev'));
    }
    if (nextEl) {
      nextEl.disabled = (idx >= NOTES.length - 1);
      nextEl.setAttribute('aria-label', t('coach_next'));
    }
    if (closeEl) closeEl.setAttribute('aria-label', t('coach_dismiss'));
  }

  function render() {
    if (idx < 0) { box.hidden = true; return; }
    var note = NOTES[idx];
    eyebrow.textContent = t('coach_note') + ' ' + (idx + 1) + '/' + NOTES.length;
    bodyEl.textContent = note.vals ? fill(note.key, note.vals()) : t(note.key);
    syncNav();
    box.hidden = false;
  }

  function show(next) {
    clearTimer();
    paused = false;
    idx = next;
    save(idx);
    if (idx < 0) { box.hidden = true; return; }
    render();
    swap();
    var note = NOTES[idx], after = note.next;
    /* A note is not jumpable until it has been on screen long enough to
     * read. Without this an action signal arriving in the same breath as the
     * note that asked for it would blank the note before anyone saw it —
     * most obviously on the drag note, which appears while the player's hand
     * is already on a handle. */
    jumpable = false;
    floorTimer = window.setTimeout(function () {
      jumpable = true; floorTimer = null;
    }, note.floor || FLOOR);
    /* Manual mode suspends the dwell timers: once the player is steering,
     * having a note replaced under them mid-sentence is the exact thing the
     * arrows exist to prevent. */
    if (!manual && note.dwell > 0) {
      timer = window.setTimeout(function () { show(after); }, note.dwell);
    }
  }

  function finish() { show(-1); }

  /* One-shot interrupt. Suspends the current note, says the thing, then puts
   * the sequence back with a full dwell rather than the remainder — the
   * player's attention was elsewhere for nine seconds, so the note they come
   * back to is effectively new. */
  function interrupt(key) {
    if (idx < 0) return;
    clearTimer();
    paused = true;
    eyebrow.textContent = t('coach_note');
    bodyEl.textContent = t(key);
    syncNav();
    box.hidden = false;
    swap();
    timer = window.setTimeout(function () { if (paused) show(idx); }, 9000);
  }

  /* Manual mode also switches off the action jumps, not just the timers. The
   * player who paged back to re-read note 2 is reading, and having a stray
   * handle-drag throw them forward to note 3 would undo the thing they just
   * asked for. Edits are still counted, because the steep interrupt is gated
   * on having made one. */
  function signal(kind) {
    if (idx < 0) return;
    if (kind === 'edit') edits++;
    if (paused || manual) return;    /* the interrupt, or the player, has the floor */
    if (!jumpable) return;           /* note hasn't been readable long enough yet */
    var to = NOTES[idx][kind === 'edit' ? 'onEdit' : 'onAdd'];
    if (to != null && to !== idx) show(to);
  }

  /* Arrows. The first press is what puts the sequence into manual mode; from
   * then on nothing advances the note except another press. */
  function nav(delta) {
    if (idx < 0) return;
    manual = true;
    var to = idx + delta;
    if (to < 0 || to >= NOTES.length) { paused = false; render(); return; }
    show(to);
  }

  /* --- signal 1: the player altered the alignment ----------------------- */
  /* Armed by a canvas gesture, disarmed shortly after it ends. See the note
   * on `edit` in the header comment for why the gate is necessary. 500 ms
   * covers the re-analysis at full precision that lands after a drag. */
  var armed = false, disarm = null;
  function arm()   { armed = true; if (disarm) { clearTimeout(disarm); disarm = null; } }
  function unarm() {
    if (disarm) clearTimeout(disarm);
    disarm = window.setTimeout(function () { armed = false; disarm = null; }, 500);
  }
  canvas.addEventListener('pointerdown',   arm,   true);
  canvas.addEventListener('pointerup',     unarm, true);
  canvas.addEventListener('pointercancel', unarm, true);

  if (mLength && window.MutationObserver) {
    var lastLen = mLength.textContent;
    new MutationObserver(function () {
      var v = mLength.textContent;
      if (v === lastLen) return;
      lastLen = v;
      if (armed) signal('edit');
    }).observe(mLength, { childList: true, characterData: true, subtree: true });
  } else {
    console.warn('[Coach] #m-length not observable — notes will advance on their timers only.');
  }

  /* + / − Segment move the road without a canvas gesture, so they report
   * directly. Reset deliberately doesn't: undoing your work is not progress
   * through a tutorial. */
  document.addEventListener('click', function (ev) {
    // ev.target is EventTarget, which has no closest(). The runtime guard
    // below is the real check — this cast only lets the compiler see it.
    var el = ev.target as Element | null;
    if (!el || typeof el.closest !== 'function') return;
    if (el.closest('#btn-add') || el.closest('#btn-remove')) signal('edit');
    /* Anyone reaching for Submit has stopped needing to be taught. */
    if (el.closest('#btn-submit') || el.closest('#btn-submit-top')) finish();
  });

  /* --- signal 2: a control point appeared ------------------------------- */
  if (cpUsed && window.MutationObserver) {
    var lastCp = null;
    new MutationObserver(function () {
      var n = parseInt(cpUsed.textContent, 10);
      if (!isFinite(n)) return;
      if (lastCp !== null && n > lastCp) signal('add');
      lastCp = n;
    }).observe(cpUsed, { childList: true, characterData: true, subtree: true });
  }

  /* --- signal 3: the road stopped being buildable ------------------------ */
  /* Gated on edits > 0. A generated puzzle whose default straight line is
   * already too steep would otherwise open the game by blaming the player
   * for something they haven't done yet. */
  if (warnEl && window.MutationObserver) {
    new MutationObserver(function () {
      if (steepUsed || edits === 0) return;
      if (warnEl.style.display === 'block') { steepUsed = true; interrupt('coach_steep'); }
    }).observe(warnEl, { attributes: true, attributeFilter: ['style'] });
  }

  /* --- navigation, dismissal and locale ---------------------------------- */
  if (prevEl)  prevEl.addEventListener('click', function () { nav(-1); });
  if (nextEl)  nextEl.addEventListener('click', function () { nav(1); });
  if (closeEl) closeEl.addEventListener('click', finish);

  document.addEventListener('localechange', function () {
    if (idx < 0) return;
    /* The interrupt owns the body text, so a locale switch while it's up
     * re-translates only the controls; the body follows when it restores. */
    if (paused) syncNav(); else render();
  });

  show(load());

  return {
    finish: finish,
    next: function () { nav(1); },
    prev: function () { nav(-1); },
    /* Development affordance: puts the sequence back to note 1 in place,
     * re-arms the steep interrupt and hands control back to the timers. */
    restart: function () {
      manual = false; steepUsed = false; edits = 0;
      show(0);
    },
    get step() { return idx; },
    get manual() { return manual; }
  };
})();
